import React from "react"
import PropTypes from "prop-types"
import { faLoveseat, faUserNurse } from "@fortawesome/pro-light-svg-icons"
import { faCheck } from "@fortawesome/free-solid-svg-icons"
import classNames from "classnames"
import Card from "../../layout/card"
import PricePerWeek, { formatNumberAsGbp } from "./PricePerWeek/PricePerWeek"
import StaticImage from "../../StaticImage"

import {
  BodyText,
  InlineText,
  Strong,
  SubTitle,
  Title,
} from "../../Typography/Typography"
import ListItemWithIcon from "../../ListItemWithIcon/ListItemWithIcon"
import { renderIf } from "../../FeatureToggle/FeatureToggle"
import {
  averageCareHomeCosts,
  bodyText,
  container,
  imgContainer,
  marginBottom,
} from "./AverageCareHomeCosts.module.scss"
import SmartLink from "../../layout/SmartLink/SmartLink"
import LineBreak from "../../LineBreak/LineBreak"
import Separator from "../../Separator/Separator"

export const careHomeCostPath = {
  Scotland:
    "https://www.caresourcer.com/caresupport/financing-care/care-home-fees-nursing-home-funding-scotland/",
  England:
    "https://www.caresourcer.com/caresupport/financing-care/care-home-costs-fees-england/",
}

export const englandAssetsValue = 23250
export const scotlandAssetsValue = 28500

export const areaData = {
  "North East": {
    residential: 574,
    nursing: 716,
  },
  "North West": {
    residential: 542,
    nursing: 852,
  },
  "Yorkshire and The Humber": {
    residential: 591,
    nursing: 810,
  },
  "East Midlands": {
    residential: 623,
    nursing: 812,
  },

  "West Midlands": {
    residential: 597,
    nursing: 920,
  },
  "East of England": {
    residential: 662,
    nursing: 1024,
  },
  London: {
    residential: 744,
    nursing: 902,
  },
  "South East": {
    residential: 775,
    nursing: 1019,
  },
  "South West": {
    residential: 700,
    nursing: 1015,
  },
  Scotland: {
    residential: 761,
    nursing: 845,
  },
}

const careNeedsAssessmentPath =
  "https://www.caresourcer.com/caresupport/understanding-care/care-needs-assessment"

export default function AverageCareHomeCosts({ area }) {
  const regionData = areaData[area]
  const RenderIfScotland = renderIf(area === "Scotland")
  const RenderIfEngland = renderIf(area !== "Scotland")

  const careNeedsAssessmentClickEvent = {
    name:
      "SEO - Filtered Directory - Average Care Home Price - Care Needs Assessment Clicked",
    properties: {
      "Geo Location": area,
    },
  }

  const careHomesCostClickEvent = {
    name: "SEO - Filtered Directory - Funding Article Clicked",
    properties: {
      "Geo Location": area,
    },
  }

  return (
    <Card noPadding extraClasses={averageCareHomeCosts}>
      <StaticImage name="seeker_on_laptop" className={imgContainer} />
      <div className={container}>
        <Title>Average care home costs in {area}</Title>
        <BodyText>
          Average costs based on LaingBuisson Care of Older People UK report
          2019
        </BodyText>
      </div>
      <ul>
        <ListItemWithIcon
          icon={faLoveseat}
          iconSize="2x"
          className={marginBottom}
        >
          <BodyText>Residential care homes</BodyText>
          <PricePerWeek price={regionData?.residential || 0} />
        </ListItemWithIcon>
        <ListItemWithIcon icon={faUserNurse} iconSize="2x">
          <BodyText>Nursing care homes</BodyText>
          <PricePerWeek price={regionData?.nursing || 0} />
        </ListItemWithIcon>
      </ul>
      <Separator />
      <RenderIfScotland>
        <div className={classNames(container)}>
          <SubTitle>Funding for care in Scotland</SubTitle>
          <BodyText>
            Your care home costs could be partially funded by the council. To
            quality for this, you must first arrange a{" "}
            <SmartLink
              to={careNeedsAssessmentPath}
              clickEvent={careNeedsAssessmentClickEvent}
            >
              <InlineText brand>care needs assessment</InlineText>
            </SmartLink>
            .
          </BodyText>
        </div>
        <ul>
          <ListItemWithIcon icon={faCheck} className={marginBottom}>
            <BodyText>
              <Strong>Free personal and/or nursing care funding</Strong> is
              available to anyone aged 65 or over, regardless of their financial
              situation.
            </BodyText>
          </ListItemWithIcon>
          <ListItemWithIcon icon={faCheck}>
            <BodyText>
              Funding towards <Strong>accommodation</Strong> costs is also
              available if your assets (property & savings) are below{" "}
              <Strong>{formatNumberAsGbp(scotlandAssetsValue)}</Strong>.
            </BodyText>
          </ListItemWithIcon>
        </ul>
      </RenderIfScotland>
      <RenderIfEngland>
        <div className={classNames(container)}>
          <SubTitle>Funding for care in England</SubTitle>
          <BodyText className={bodyText}>
            If your total savings and property assets are below{" "}
            <Strong>{formatNumberAsGbp(englandAssetsValue)}</Strong>, then you
            may be able to access local authority funding towards{" "}
            <Strong>accommodation</Strong> costs, <Strong>personal care</Strong>{" "}
            and <Strong>nursing care</Strong>.
          </BodyText>
          <BodyText className={bodyText}>
            To qualify, you must first arrange a{" "}
            <SmartLink
              to={careNeedsAssessmentPath}
              clickEvent={careNeedsAssessmentClickEvent}
            >
              <InlineText brand>care needs assessment</InlineText>
            </SmartLink>
            .
          </BodyText>
        </div>
      </RenderIfEngland>
      <div className={classNames(container)}>
        <SmartLink
          to={careHomeCostPath[area] || careHomeCostPath.England}
          chevron
          clickEvent={careHomesCostClickEvent}
        >
          <InlineText brand>
            Read about care home costs in <LineBreak onDesktopOnly />
            {area === "Scotland" ? "Scotland" : "England"}
          </InlineText>
        </SmartLink>
      </div>
    </Card>
  )
}

AverageCareHomeCosts.propTypes = {
  area: PropTypes.oneOf(Object.keys(areaData)).isRequired,
  branchRecordCounts: PropTypes.shape({
    countOfProvidersWithPhotos: PropTypes.number.isRequired,
    countOfProvidersWithIntroBlurb: PropTypes.number.isRequired,
    countOfProvidersWithPrices: PropTypes.number.isRequired,
    countOfPremiumProviders: PropTypes.number.isRequired,
  }).isRequired,
}
